import {
  Alert,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  TextInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useEffect, useImperativeHandle } from "react"
import { Trans, useTranslation } from "react-i18next"
import { getErrorMessage } from "../utils"
import {
  useCreateAgencyUser,
  useUpdateAgencyUser,
} from "../hooks/useAgencyUsers"
import type { AgencyUser, AgencyUserFormValues } from "../models/agencyUser"

interface Props {
  user?: AgencyUser | null
  onSuccess: () => void
}

const AgencyUserFormContent = forwardRef<{ submit: () => void }, Props>(({ user, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!user
  const createMutation = useCreateAgencyUser()
  const updateMutation = useUpdateAgencyUser()

  const form = useForm<AgencyUserFormValues>({
    initialValues: {
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      password: "",
    },
    validate: {
      username: (v) =>
        v?.length >= 2 ? null : t("agencyUsers.validation.usernameRequired"),
      first_name: (v) =>
        v?.length ? null : t("agencyUsers.validation.firstNameRequired"),
      last_name: (v) =>
        v?.length ? null : t("agencyUsers.validation.lastNameRequired"),
      email: (v) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
          ? null
          : t("agencyUsers.validation.emailInvalid"),
      ...(isEdit
        ? {}
        : {
            password: (v: string) =>
              v?.length >= 6 ? null : t("agencyUsers.validation.passwordMin"),
          }),
    },
  })

  useEffect(() => {
    if (user) {
      form.setValues({
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name ?? "",
        email: user.email,
        password: "",
      })
    }
  }, [user])

  const handleSubmit = (values: AgencyUserFormValues) => {
    if (isEdit && user) {
      updateMutation.mutate(
        { id: user.id, values },
        {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("agencyUsers.updateSuccess"),
            })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("agencyUsers.createSuccess"),
          })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

  return (
    <form onSubmit={handleFormSubmit}>
      {(createMutation.isError || updateMutation.isError) && (
        <Alert
          color="red"
          mb="md"
          withCloseButton
          onClose={() => {
            createMutation.reset()
            updateMutation.reset()
          }}
        >
          {createMutation.error?.message || updateMutation.error?.message}
        </Alert>
      )}
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("agencyUsers.username")}
              {...form.getInputProps("username")}
              autoFocus={!isEdit}
            />
            <TextInput
              label={t("agencyUsers.email")}
              {...form.getInputProps("email")}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("agencyUsers.firstName")}
              {...form.getInputProps("first_name")}
            />
            <TextInput
              label={t("agencyUsers.lastName")}
              {...form.getInputProps("last_name")}
            />
          </SimpleGrid>
          {!isEdit && (
            <TextInput
              label={t("agencyUsers.password")}
              type="password"
              autoComplete="new-password"
              {...form.getInputProps("password")}
            />
          )}
        </Stack>
      </Paper>

    </form>
  )
})

export default AgencyUserFormContent
